import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../shared/config';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../ui/input';
import { Stethoscope, Plus, KeyRound, UserX, UserCheck, Loader2, X, Check } from 'lucide-react';
import { sortStaff } from '../../lib/staffSort';
import { hasDroit } from '../../lib/droits';

const vide = { email: '', password: '', nom: '', prenom: '', specialite: '', etablissement: '', telephone: '' };

export const CorrespondantsManager = () => {
  const { user } = useAuth();
  const [medecins, setMedecins] = useState([]);
  const [types, setTypes] = useState([]);
  const [autorises, setAutorises] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode:'ajout'|'edit', data}
  const [saving, setSaving] = useState(false);
  const [newType, setNewType] = useState({ categorie: 'chirurgie', nom: '' });

  const load = async () => {
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/correspondants/medecins`),
        axios.get(`${API}/correspondants/types`),
      ]);
      setMedecins(m.data); setTypes(t.data);
      if (hasDroit(user, 'correspondants', 'habilitations')) {
        const [cfg, us] = await Promise.all([axios.get(`${API}/correspondants/config`), axios.get(`${API}/users`)]);
        setAutorises(cfg.data.autorises || []);
        setEmployes(us.data.filter(u => u.role !== 'Directeur'));
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Accès non autorisé'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveMedecin = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'ajout') await axios.post(`${API}/correspondants/medecins`, modal.data);
      else await axios.put(`${API}/correspondants/medecins/${modal.data.id}`, modal.data);
      toast.success(modal.mode === 'ajout' ? 'Médecin correspondant créé' : 'Modifications enregistrées');
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const toggleActif = async (m) => {
    await axios.put(`${API}/correspondants/medecins/${m.id}`, { actif: !m.actif });
    toast.success(m.actif ? 'Compte désactivé' : 'Compte réactivé'); load();
  };

  const toggleAutorise = async (uid) => {
    const next = autorises.includes(uid) ? autorises.filter(x => x !== uid) : [...autorises, uid];
    setAutorises(next);
    await axios.put(`${API}/correspondants/config`, { autorises: next });
  };

  const addType = async () => {
    if (!newType.nom.trim()) return;
    await axios.post(`${API}/correspondants/types`, newType);
    setNewType(t => ({ ...t, nom: '' })); load();
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#1173D4]" /></div>;

  return (
    <div className="space-y-5" data-testid="correspondants-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#0B1F3A] flex items-center gap-2"><Stethoscope className="h-5 w-5 text-[#1173D4]" /> Médecins correspondants</h2>
          <p className="text-xs text-gray-400">Comptes externes avec accès strictement limité à leur portail sécurisé.</p>
        </div>
        {hasDroit(user, 'correspondants', 'gerer_comptes') && (
          <button onClick={() => setModal({ mode: 'ajout', data: { ...vide } })}
            className="px-3 py-2 rounded-xl bg-[#1173D4] text-white text-xs font-bold hover:bg-[#0d5cab] flex items-center gap-1.5" data-testid="corr-admin-add">
            <Plus className="h-4 w-4" /> Ajouter un médecin
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        <table className="w-full text-left">
          <thead><tr className="text-[10px] uppercase text-gray-400 border-b">
            <th className="px-4 py-2.5">Médecin</th><th className="px-2 py-2.5">Spécialité</th><th className="px-2 py-2.5 hidden sm:table-cell">Établissement</th>
            <th className="px-2 py-2.5">Demandes</th><th className="px-2 py-2.5 hidden sm:table-cell">Dernière activité</th><th className="px-2 py-2.5">Statut</th>{hasDroit(user, 'correspondants', 'gerer_comptes') && <th className="px-2 py-2.5 text-right pr-4">Actions</th>}
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {medecins.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-xs text-gray-400 text-center">Aucun médecin correspondant pour le moment.</td></tr>}
            {sortStaff(medecins).map(m => (
              <tr key={m.id} className="text-sm" data-testid={`corr-admin-row-${m.id}`}>
                <td className="px-4 py-2.5"><p className="font-semibold text-gray-800">Dr {m.prenom} {m.nom}</p><p className="text-[10px] text-gray-400">{m.email} · {m.telephone}</p></td>
                <td className="px-2 py-2.5 text-xs text-gray-600">{m.specialite || '—'}</td>
                <td className="px-2 py-2.5 text-xs text-gray-600 hidden sm:table-cell">{m.etablissement || '—'}</td>
                <td className="px-2 py-2.5 text-xs font-bold text-[#1173D4]">{m.nb_demandes}</td>
                <td className="px-2 py-2.5 text-[11px] text-gray-400 hidden sm:table-cell">{m.derniere_activite ? new Date(m.derniere_activite).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="px-2 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.actif ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{m.actif ? 'Actif' : 'Désactivé'}</span></td>
                {hasDroit(user, 'correspondants', 'gerer_comptes') && (
                  <td className="px-2 py-2.5 pr-4">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setModal({ mode: 'edit', data: { id: m.id, nom: m.nom, prenom: m.prenom, telephone: m.telephone || '', specialite: m.specialite, etablissement: m.etablissement, password: '' } })}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-[#1173D4] hover:bg-blue-50" title="Modifier / réinitialiser l'accès" data-testid={`corr-admin-edit-${m.id}`}><KeyRound className="h-4 w-4" /></button>
                      <button onClick={() => toggleActif(m)} className={`p-1.5 rounded-lg ${m.actif ? 'text-gray-400 hover:text-red-500 hover:bg-red-50' : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'}`}
                        title={m.actif ? 'Désactiver' : 'Réactiver'} data-testid={`corr-admin-toggle-${m.id}`}>{m.actif ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Types configurables */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-bold text-[#0B1F3A] mb-2">Types de chirurgies & examens</h3>
          {hasDroit(user, 'correspondants', 'types') && (
            <div className="flex gap-1.5 mb-3">
              <select value={newType.categorie} onChange={e => setNewType(t => ({ ...t, categorie: e.target.value }))} className="h-8 text-xs border rounded-lg px-1 bg-white font-semibold">
                <option value="chirurgie">Chirurgie</option><option value="examen">Examen</option>
              </select>
              <Input value={newType.nom} onChange={e => setNewType(t => ({ ...t, nom: e.target.value }))} placeholder="Nouveau type…" className="h-8 text-xs" data-testid="corr-type-input" />
              <button onClick={addType} className="px-2.5 rounded-lg bg-[#1173D4] text-white text-xs font-bold" data-testid="corr-type-add"><Plus className="h-4 w-4" /></button>
            </div>
          )}
          {['chirurgie', 'examen'].map(cat => (
            <div key={cat} className="mb-2">
              <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">{cat === 'chirurgie' ? 'Chirurgies' : 'Examens'}</p>
              <div className="flex flex-wrap gap-1.5">
                {types.filter(t => t.categorie === cat).map(t => (
                  <span key={t.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border ${t.actif ? 'bg-blue-50 text-[#1173D4] border-blue-100' : 'bg-gray-50 text-gray-400 border-gray-200 line-through'}`}>
                    {t.nom}
                    {hasDroit(user, 'correspondants', 'types') && <button onClick={async () => { await axios.put(`${API}/correspondants/types/${t.id}`, { actif: !t.actif }); load(); }} title={t.actif ? 'Désactiver' : 'Réactiver'} className="hover:opacity-70">{t.actif ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />}</button>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Habilitations (Directeur) */}
        {hasDroit(user, 'correspondants', 'habilitations') && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <h3 className="text-sm font-bold text-[#0B1F3A]">Employés habilités</h3>
            <p className="text-[11px] text-gray-400 mb-2">Cochez les employés qui peuvent voir les correspondants et leurs demandes (en plus de vous).</p>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {sortStaff(employes).map(e => (
                <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={autorises.includes(e.id)} onChange={() => toggleAutorise(e.id)} className="h-4 w-4 accent-[#1173D4]" data-testid={`corr-habilite-${e.id}`} />
                  <span className="text-xs font-semibold text-gray-700">{e.prenom} {e.nom}</span>
                  <span className="text-[10px] text-gray-400 ml-auto">{e.role}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl p-5 max-w-md w-full shadow-xl space-y-2.5" onClick={e => e.stopPropagation()} data-testid="corr-admin-modal">
            <h3 className="font-bold text-[#0B1F3A]">{modal.mode === 'ajout' ? 'Ajouter un médecin correspondant' : `Modifier — Dr ${modal.data.prenom} ${modal.data.nom}`}</h3>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Prénom" value={modal.data.prenom} onChange={e => setModal(m => ({ ...m, data: { ...m.data, prenom: e.target.value } }))} className="h-9 text-sm" data-testid="corr-modal-prenom" />
              <Input placeholder="Nom" value={modal.data.nom} onChange={e => setModal(m => ({ ...m, data: { ...m.data, nom: e.target.value } }))} className="h-9 text-sm" data-testid="corr-modal-nom" />
            </div>
            {modal.mode === 'ajout' && <Input placeholder="E-mail de connexion" value={modal.data.email} onChange={e => setModal(m => ({ ...m, data: { ...m.data, email: e.target.value } }))} className="h-9 text-sm" data-testid="corr-modal-email" />}
            <Input placeholder={modal.mode === 'ajout' ? 'Mot de passe (12 caractères min.)' : 'Nouveau mot de passe (laisser vide pour conserver)'} type="text" value={modal.data.password} onChange={e => setModal(m => ({ ...m, data: { ...m.data, password: e.target.value } }))} className="h-9 text-sm" data-testid="corr-modal-password" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Spécialité" value={modal.data.specialite} onChange={e => setModal(m => ({ ...m, data: { ...m.data, specialite: e.target.value } }))} className="h-9 text-sm" />
              <Input placeholder="Téléphone" value={modal.data.telephone} onChange={e => setModal(m => ({ ...m, data: { ...m.data, telephone: e.target.value } }))} className="h-9 text-sm" />
            </div>
            <Input placeholder="Établissement" value={modal.data.etablissement} onChange={e => setModal(m => ({ ...m, data: { ...m.data, etablissement: e.target.value } }))} className="h-9 text-sm" />
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setModal(null)} className="px-3 py-1.5 rounded-xl border text-xs font-semibold text-gray-600">Annuler</button>
              <button onClick={saveMedecin} disabled={saving} className="px-4 py-1.5 rounded-xl bg-[#1173D4] text-white text-xs font-bold hover:bg-[#0d5cab] disabled:opacity-50" data-testid="corr-modal-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : modal.mode === 'ajout' ? 'Créer le compte' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorrespondantsManager;
